import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import { selectCurrentVTOSession } from '../../../redux/slices/vtoSlice';
import { useCreateShareBundleMutation } from '../../../redux/apis/endpoints/shareBundleApi';
import './ShareModal.css';

const ShareModal = ({ isOpen, onClose, onDownload }) => {
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [createShareBundle, { isLoading: isCreatingShare }] = useCreateShareBundleMutation();
  const currentSession = useSelector(selectCurrentVTOSession);

  // Auto-hide toast
  useEffect(() => {
    if (showCopiedToast) {
      const t = setTimeout(() => {
        setShowCopiedToast(false);
        onClose();
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [showCopiedToast, onClose]);

  const handleCopyShareLink = useCallback(async () => {
    if (isCreatingShare) return;

    const vtoResultId = currentSession?.vtoResultId;
    if (!vtoResultId) {
      console.warn('No VTO result ID to share');
      return;
    }

    try {
      // Single looks go through the same bundle system as everything else, so
      // the recipient gets the look image in their clothing panel too
      const result = await createShareBundle({
        sourceType: 'vto_result',
        vtoResultIds: [vtoResultId]
      }).unwrap();

      // Copy to clipboard
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(result.shareUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = result.shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setShowCopiedToast(true);
    } catch (error) {
      console.error('Failed to create share link:', error);
    }
  }, [currentSession?.vtoResultId, createShareBundle, isCreatingShare]);

  const handleDownloadClick = useCallback(() => {
    onDownload();
    onClose();
  }, [onDownload, onClose]);

  if (!isOpen && !showCopiedToast) return null;

  return createPortal(
    <>
      {/* Copied toast */}
      {showCopiedToast && (
        <div className="share-copied-toast">
          <span className="toast-check">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          Link copied to clipboard
        </div>
      )}

      {/* Modal overlay + sheet */}
      {isOpen && (
        <div className="share-modal-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
          <div className="share-modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="share-modal-handle" onClick={onClose} role="button" aria-label="Close" />

            <div className="share-modal-header">
              <div>
                <div className="share-modal-title">Share this look</div>
                <div className="share-modal-subtitle">Download the image or invite a friend to try it on</div>
              </div>
              <button className="share-modal-close" onClick={onClose} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Download option */}
            <button className="share-modal-option" onClick={handleDownloadClick}>
              <div className="share-modal-option-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 3v8M6 8l3 3 3-3M3 13h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <div className="share-modal-option-label">Download image</div>
                <div className="share-modal-option-desc">Save this try-on to your device</div>
              </div>
            </button>

            {/* Copy share link option */}
            <button
              className={`share-modal-option highlighted`}
              onClick={handleCopyShareLink}
              disabled={isCreatingShare || !currentSession?.vtoResultId}
            >
              <div className="share-modal-option-icon">
                {isCreatingShare ? (
                  <Icon name="spinner" loading />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M7.5 10.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M10.5 7.5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div className="share-modal-option-label">
                  {isCreatingShare ? 'Creating link...' : 'Copy share link'}
                </div>
                <div className="share-modal-option-desc">Invite a friend to try on this item</div>
              </div>
              <svg className="share-modal-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ShareModal;
